import { can, formatCode } from '@pl/domain';
import type { ReasonCode, ScanResponse, TransferDetailDto } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { qk, useMe, useSiteMap, useSkuMap, useTransfer } from '../../lib/queries';
import { Empty, ErrorNote, RefetchNote, SkuTag } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import type { LocalResult } from '../ResultPanel';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import { kit, plural, RevealedResult, ScanRedScreen, ScanSheet, waitingFor } from './scanKit';
import { BACK_TO_TRANSFERS, gateTransfer, RouteTags, useTransferScan } from './transferKit';
import s from './TransferOut.module.css';

/** What a refusal about *this box* is called on the phone; the server's message carries the detail. */
const RED_TITLE: Partial<Record<ReasonCode, string>> = {
  RESERVED_FOR_ORDERS: 'Reserved for orders',
  PICKED_ELSEWHERE: 'On another order',
  ALREADY_SHIPPED: 'Already shipped',
  OTHER_SITE: 'Wrong site',
  NOT_RECEIVED: 'Not in stock here',
  IN_TRANSIT: 'Already on a transfer',
  WRITTEN_OFF: 'Written off',
  VOIDED: 'Voided sticker',
  UNKNOWN_CODE: 'Unknown sticker',
  INVALID_CODE: 'Unknown sticker',
};

const PUT_BACK = 'Nothing was changed. Put this box back.';

/**
 * Scanning boxes onto a transfer at the sending site, then sending it.
 *
 * Send is locked on the server's count and on the queue, never on how many times Add was pressed:
 * a scan still on the phone is a box on the dock the server has not heard of, and sending past it
 * would leave that box behind in stock while it rides the truck. Cancel is locked for the same
 * reason — a box the phone still holds would be put back in stock on paper and not on the floor.
 */
export function TransferOut() {
  const { transferId = '' } = useParams();
  const me = useMe();
  const { site } = useScanSite();
  const qc = useQueryClient();
  const nav = useNavigate();
  const query = useTransfer(transferId);
  const skuById = useSkuMap();
  const siteById = useSiteMap();
  const reasonId = useId();

  const t = query.data;
  const toName = (t && siteById.get(t.toSiteId)?.name) ?? 'the other site';
  const scan = useTransferScan(transferId, t?.code, {
    mode: 'transfer_out',
    refused: 'Not added',
    locks: 'Send',
    refusalNote: PUT_BACK,
    titles: RED_TITLE,
    fallbackTitle: "Can't send this box",
    accepted: (response, code) => describeAccepted(response, toName, code),
    // Reservations win (§10.3), and a lead may overrule exactly that one refusal.
    overridable: (response) => response.reasonCode === 'RESERVED_FOR_ORDERS',
  });
  const { pending, red, setRed } = scan;

  /** The box a lead is overruling a reservation for, while they give the reason. */
  const [overrideCode, setOverrideCode] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  /** The server's own answer to Send; the screen after it is not a query. */
  const [sent, setSent] = useState<TransferDetailDto | null>(null);

  const lead = Boolean(me.data && can(me.data.role, 'override'));
  const keepScanning = useRef<HTMLButtonElement>(null);

  /**
   * A send whose answer was lost on the way back is retried into a 409 about the send it has just
   * made. The server is asked what became of the transfer, and only a transfer that really is on
   * its way is taken as the answer; anything else keeps the refusal in front of the worker.
   */
  const send = useMutation({
    mutationFn: async () => {
      try {
        return await api.post<TransferDetailDto>(`/transfers/${transferId}/send`);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'TRANSFER_NOT_DRAFT') throw err;
        const now = await api.get<TransferDetailDto>(`/transfers/${transferId}`).catch(() => null);
        if (now?.status !== 'in_transit') throw err;
        return now;
      }
    },
    onSuccess: (dto) => {
      setSent(dto);
      qc.setQueryData(qk.transfer(transferId), dto);
      void qc.invalidateQueries({ queryKey: ['transfers'] });
    },
  });
  /** As with Send: a lost answer retried into a 409 is checked with the server, and a transfer that really is canceled is the answer. */
  const cancel = useMutation({
    mutationFn: async () => {
      try {
        return await api.post<TransferDetailDto>(`/transfers/${transferId}/cancel`);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'TRANSFER_NOT_DRAFT') throw err;
        const now = await api.get<TransferDetailDto>(`/transfers/${transferId}`).catch(() => null);
        if (now?.status !== 'cancelled') throw err;
        return now;
      }
    },
    onSuccess: (dto) => {
      qc.setQueryData(qk.transfer(transferId), dto);
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      nav('/scan/transfer', { replace: true });
    },
  });

  if (sent) {
    const to = siteById.get(sent.toSiteId);
    return (
      <div className={s.sentScreen} role="status">
        <div className={s.sentTick}>
          <Icon name="check" size={58} />
        </div>
        <h2 className={s.sentTitle}>{`Sent · ${sent.code}`}</h2>
        <p className={s.sentWhat}>{`${plural(sent.totals.sent, 'box', 'boxes')} on the way to ${to?.name ?? 'the other site'}`}</p>
        <div className={s.sentFoot}>
          <Link to="/scan/transfer" className={`bigbtn ${s.sentLink}`}>
            Back to transfers
          </Link>
        </div>
      </div>
    );
  }

  const gate = gateTransfer(site, query);
  if (gate || !site || !t) return gate;

  const from = siteById.get(t.fromSiteId);
  const to = siteById.get(t.toSiteId);

  if (t.fromSiteId !== site.id) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty title={`${t.code} sends from ${from?.name ?? 'another site'}`} description="Switch this phone to that site to scan boxes onto it." />
      </div>
    );
  }
  if (t.status !== 'draft') {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title={t.status === 'cancelled' ? `${t.code} was canceled` : `${t.code} was sent`}
          description={t.status === 'cancelled' ? 'Its boxes went back into stock.' : `It is no longer taking boxes. ${to ? `${to.name} scans it in.` : ''}`}
          action={
            <Link to="/scan/transfer" className="bigbtn ghost">
              Back to transfers
            </Link>
          }
        />
      </div>
    );
  }

  // `null` while a SKU on the transfer is being counted: the server leaves the number out.
  const n = t.totals.picked;
  const boxes = n === null ? 'boxes' : plural(n, 'box', 'boxes');
  const lines = t.lines.filter((l) => l.countsHidden || (l.picked ?? 0) > 0);
  const busy = send.isPending || cancel.isPending;
  const sendLocked = pending > 0 || n === 0;
  const lockNotes = [n === 0 && pending === 0 ? 'Scan a box or a pallet to add it' : null, pending > 0 ? waitingFor(pending) : null].filter((x): x is string => x !== null);

  return (
    <>
      <div className={`scan-body pt ${s.outBody}`}>
        <div className={s.outHead}>
          <div className={s.outIdent}>
            <div className={`mono ${s.outCode}`}>{t.code}</div>
            <div className={s.outTo}>{`To ${toName}`}</div>
          </div>
          <RouteTags from={from} to={to} />
        </div>
        {to && !to.live && <p className={s.notLive}>{`${to.name} is not live yet: boxes will wait in transit until it is.`}</p>}
        <RefetchNote queries={[query]} />

        {/* The scanner stops while a red outcome or the override sheet holds the screen, so the next
            sticker cannot slip past unread and a wedge scanner's Enter cannot double as an answer —
            and while Send or Cancel is on its way, since a box read then is refused behind the Sent
            screen and rides the truck while it stays in stock on paper. */}
        <ScannerView onCode={scan.handleCode} enabled={red === null && overrideCode === null && !send.isPending && !cancel.isPending} />
        <RevealedResult result={scan.shownResult} revealOn={scan.result} />

        <div className="sect">
          In this transfer<span className="r">{n === null ? 'Counting · hidden' : plural(n, 'box', 'boxes')}</span>
        </div>
        {lines.length === 0 ? (
          <p className={s.outEmpty}>Nothing scanned yet. A pallet sticker adds every box on it that is in stock here.</p>
        ) : (
          <ul className={s.groupList} aria-label="In this transfer">
            {lines.map((l) => (
              <li key={l.skuId} className={s.groupRow}>
                <span className={`skux ${s.groupSku}`}>
                  <SkuTag sku={skuById.get(l.skuId)} withCode />
                </span>
                <span className={`mono ${s.groupCount}`}>{l.countsHidden ? 'Counting · hidden' : l.picked}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="scan-foot">
        {lockNotes.length > 0 && (
          <p className={kit.tkFootNote}>
            {lockNotes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </p>
        )}
        <ErrorNote error={send.error} />
        <button type="button" className={`bigbtn accent${sendLocked ? ' locked' : ''}`} disabled={sendLocked || busy} onClick={() => send.mutate()}>
          {sendLocked && <Icon name="lock" />}
          {`Send ${boxes} to ${toName}`}
        </button>
        <button type="button" className={s.cancelLink} disabled={pending > 0 || busy} onClick={() => setCancelOpen(true)}>
          Cancel transfer
        </button>
      </div>

      <ScanSheet
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          cancel.reset();
        }}
        busy={cancel.isPending}
        safeFocus={keepScanning}
        title={`Cancel ${t.code}?`}
        lead={n === null || n > 0 ? `The ${boxes} go back into stock at ${from?.name ?? 'this site'}.` : 'Nothing has been scanned onto it yet.'}
        actions={
          <>
            <ErrorNote error={cancel.error} />
            <button type="button" className={`bigbtn ${kit.tkDanger}`} disabled={cancel.isPending || pending > 0} onClick={() => cancel.mutate()}>
              Cancel transfer
            </button>
            <button type="button" ref={keepScanning} className="bigbtn ghost" disabled={cancel.isPending} onClick={() => setCancelOpen(false)}>
              Keep scanning
            </button>
          </>
        }
      />

      <ScanSheet
        open={overrideCode !== null}
        onClose={() => {
          setOverrideCode(null);
          setReason('');
        }}
        title="Send anyway"
        lead={`${formatCode(overrideCode ?? '')} goes on ${t.code} even though orders here are counting on it. Your name and reason are logged.`}
        actions={
          <>
            <button
              type="button"
              className="bigbtn"
              disabled={reason.trim().length === 0}
              onClick={() => {
                const code = overrideCode!;
                const why = reason.trim();
                setOverrideCode(null);
                setReason('');
                void scan.submit(code, { reason: why });
              }}
            >
              Send anyway
            </button>
            <button
              type="button"
              className="bigbtn ghost"
              onClick={() => {
                setOverrideCode(null);
                setReason('');
              }}
            >
              Put it back
            </button>
          </>
        }
      >
        <label className={kit.tkCap} htmlFor={reasonId}>
          Reason
        </label>
        <textarea
          id={reasonId}
          className={kit.tkReason}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder="Why can this box go?"
        />
      </ScanSheet>

      <ScanRedScreen back={BACK_TO_TRANSFERS}
        red={red}
        resumeLabel="Got it, put it back"
        onDismiss={() => setRed(null)}
        secondary={
          red?.overridable && lead
            ? {
                label: 'Override: send anyway',
                onClick: () => {
                  // One commit: the red screen goes and the sheet arrives, so focus lands in the sheet.
                  setOverrideCode(red.overridable!);
                  setRed(null);
                },
              }
            : undefined
        }
      />
    </>
  );
}

/** A box, or a whole pallet, now on the transfer. */
function describeAccepted(response: ScanResponse, toName: string, pretty: string | undefined): LocalResult {
  // A pallet's count is the server's own sentence ("… · 4 boxes added", or "3 of 5" when some of it
  // is committed elsewhere). `pallet.boxCount` is what the pallet holds, not what was added, and the
  // two differ exactly when the difference matters.
  if (response.pallet && !response.box) return { tone: 'ok', title: 'Pallet added', message: response.message, code: pretty };
  return { tone: 'ok', title: response.message, message: `Going to ${toName}.`, code: pretty };
}
